/**
 * documentCategory — single source of truth for the folder bucket a
 * SiteDocument lands in.
 *
 * Used by:
 *   - DocumentLibrary (folder grouping in the library tree)
 *   - MoveFolderDialog (resolving a doc's current auto-derived bucket)
 *   - store folder rename (matching docs that sit in the renamed bucket)
 *
 * The user's explicit `doc.folder` always wins; otherwise the rules
 * below match against tags first (richer signal) and fall back to
 * file extension. Keep the rule set in sync with the buckets the
 * library UI shows the user — adding a tag mapping here without
 * adding the bucket to the library would create a folder the user
 * can never navigate to.
 */
import type { SiteDocument } from '../types/siteRag';
import { resolveSiteDocumentFileName } from './site/resolveSiteDocuments';

export function categorize(doc: SiteDocument): string {
  if (doc.folder) return doc.folder;
  const tags = (doc.tags || []).map(t => t.toLowerCase());
  const resolvedName = resolveSiteDocumentFileName(doc, doc?.id);
  const ext = resolvedName.includes('.') ? (resolvedName.split('.').pop()?.toLowerCase() || '') : '';
  if (tags.some(t => t.includes('geotechnical') || t.includes('soil') || t.includes('geology'))) return 'Geotechnical';
  if (tags.some(t => t.includes('environmental') || t.includes('epa') || t.includes('pollution'))) return 'Environmental';
  if (tags.some(t => t.includes('regulatory') || t.includes('permit') || t.includes('zoning') || t.includes('compliance'))) return 'Regulatory & Permits';
  if (tags.some(t => t.includes('grid') || t.includes('power') || t.includes('electric') || t.includes('interconnect'))) return 'Grid & Power';
  if (tags.some(t => t.includes('water') || t.includes('cooling') || t.includes('hvac'))) return 'Water & Cooling';
  if (tags.some(t => t.includes('seismic') || t.includes('earthquake') || t.includes('hazard'))) return 'Seismic & Hazards';
  if (tags.some(t => t.includes('financial') || t.includes('cost') || t.includes('lcoe') || t.includes('economic'))) return 'Financial';
  if (tags.some(t => t.includes('map') || t.includes('survey') || t.includes('topograph') || t.includes('boundary'))) return 'Survey & Maps';
  if (tags.some(t => t.includes('nuclear') || t.includes('smr') || t.includes('reactor'))) return 'Nuclear';
  if (ext === 'csv' || ext === 'xlsx' || ext === 'json') return 'Data Files';
  if (ext === 'pdf') return 'Reports & Studies';
  return 'General';
}
